import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { createSource, type TSource } from '../../entities/source';

const schema = z.object({ name: z.string().trim().min(1, 'Give it a name you will recognise') });

type TValues = z.infer<typeof schema>;

export const SourceCreate = ({
  onCreated,
  label = 'New source',
  open: opened,
  onOpenChange,
}: {
  onCreated: (source: TSource) => void;
  label?: string;
  /** Opened from elsewhere (a picker's footer): no button of its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [own, setOwn] = useState(false);
  const open = opened ?? own;
  const setOpen = (next: boolean) => {
    setOwn(next);
    onOpenChange?.(next);
  };
  const client = useQueryClient();

  const api = useApiForm<TValues>({
    schema,
    defaultValues: { name: '' },
    onSubmit: async (values) => {
      const source = await createSource({ name: values.name });
      await client.invalidateQueries({ queryKey: ['sources'] });
      setOpen(false);
      onCreated(source);
    },
  });

  const show = (next: boolean) => {
    if (next) api.form.reset();
    setOpen(next);
  };

  return (
    <>
      {opened === undefined && (
        <Button size="sm" onClick={() => show(true)}>
          {label}
        </Button>
      )}

      <Modal
        open={open}
        onOpenChange={show}
        width="md"
        title="New source"
        description="A place for one sender to post to. Its address comes back with it."
        footerNote="Its address is minted on create"
        submit={{
          label: 'Create source',
          onSubmit: () => void api.submit(),
          disabled: api.form.formState.isSubmitting,
          waiting: api.form.formState.isSubmitting,
          waitingLabel: 'Creating',
        }}
      >
        <ApiForm api={api}>
          <Field<TValues> name="name" label="Name" hint="Yours to read, not the sender’s">
            <TextInput placeholder="Stripe production" autoFocus />
          </Field>
          {/* The submit button lives in the footer, outside the form; without this one Enter would do nothing. */}
          <button type="submit" className="hidden" aria-hidden />
        </ApiForm>
      </Modal>
    </>
  );
};
